import { useEffect, useMemo, useState } from "react";
import {
  TrendingUp, Clock, Target, ListTodo, Bell, Loader2, Zap, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../config";

const STATUS_COLORS = {
  new:     "#6b7c93",
  working: "#006DFF",
  active:  "#f59e0b",
  sold:    "#10a37f",
  lost:    "#ef4444",
};

function fmtDuration(secs) {
  if (!secs || secs < 1) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)}h`;
  return `${(secs / 86400).toFixed(1)}d`;
}

function Stat({ icon: Icon, label, value, sub, color = "var(--ssc-navy)", testId }) {
  return (
    <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4 flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#6b7c93]">
        <Icon size={13} /> {label}
      </div>
      <div className="text-2xl md:text-3xl font-black tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-[#6b7c93]">{sub}</div>}
    </div>
  );
}

export default function ManagerDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get("/dealer/metrics", { params: { days } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load metrics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [days]);

  const chart = useMemo(() => {
    const series = data?.sold?.daily || [];
    const maxSold  = Math.max(1, ...series.map((d) => d.sold));
    const maxLeads = Math.max(1, ...series.map((d) => d.leads));
    return { series, maxSold, maxLeads };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-6 text-[var(--ssc-ink-2)]" data-testid="metrics-loading">
        <Loader2 className="animate-spin" /> Loading metrics…
      </div>
    );
  }
  if (err) return <div className="text-[var(--ssc-red)] font-bold p-4">{err}</div>;
  if (!data) return null;

  const respondedPct = data.total_leads
    ? Math.round((data.response_time.responded_count / data.total_leads) * 100)
    : 0;
  const totalSourceLeads = (data.by_source || []).reduce((s, x) => s + x.leads, 0) || 1;

  return (
    <div className="space-y-5" data-testid="manager-dashboard">
      {/* Window selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-black text-[var(--ssc-navy)]">Performance — last {days} days</h2>
        <div className="flex gap-1 bg-[#F4F7FB] rounded-full p-1" role="tablist" data-testid="metrics-window">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition ${days === d ? "bg-[var(--ssc-blue)] text-white shadow-md" : "text-[#6b7c93] hover:text-[var(--ssc-navy)]"}`}
              data-testid={`metrics-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Top KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          icon={TrendingUp}
          label="Sold this month"
          value={data.sold.this_month}
          sub={`${data.sold.all_time} all-time`}
          color="#10a37f"
          testId="metric-sold-month"
        />
        <Stat
          icon={Target}
          label="Total leads"
          value={data.total_leads}
          sub={`${respondedPct}% responded to`}
          color="var(--ssc-blue)"
          testId="metric-total-leads"
        />
        <Stat
          icon={Clock}
          label="Median response"
          value={fmtDuration(data.response_time.median_seconds)}
          sub={`p90 ${fmtDuration(data.response_time.p90_seconds)}`}
          color="var(--ssc-navy)"
          testId="metric-response-median"
        />
        <Stat
          icon={ListTodo}
          label="Overdue tasks"
          value={data.tasks.overdue}
          sub={`${data.tasks.open} open · ${data.tasks.done_today} done today`}
          color={data.tasks.overdue > 0 ? "#ef4444" : "#10a37f"}
          testId="metric-overdue"
        />
      </div>

      {/* Status distribution */}
      <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4" data-testid="metric-status-bar">
        <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93] mb-2">Pipeline · status mix</div>
        <div className="flex h-3 rounded-full overflow-hidden bg-[#F4F7FB]">
          {Object.entries(data.status_counts).map(([k, v]) => (
            <div
              key={k}
              className="h-full"
              style={{ width: `${(v / Math.max(1, data.total_leads)) * 100}%`, background: STATUS_COLORS[k] || "#6b7c93" }}
              title={`${k}: ${v}`}
            />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          {Object.entries(data.status_counts).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 font-bold text-[#475569]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[k] || "#6b7c93" }} />
              {k} <span className="text-[var(--ssc-navy)] tabular-nums">{v}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Sold chart + Conversion by source side-by-side */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Sold chart */}
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4" data-testid="metric-sold-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93]">Sales activity</div>
              <div className="font-extrabold text-[var(--ssc-navy)]">Sold (green) + Leads in (blue)</div>
            </div>
            <Zap className="text-[#10a37f]" size={18} />
          </div>
          <div className="flex items-end gap-0.5 h-32" role="img" aria-label="Daily sales activity">
            {chart.series.map((d) => {
              const soldH  = (d.sold  / Math.max(chart.maxSold, chart.maxLeads)) * 100;
              const leadH  = (d.leads / Math.max(chart.maxSold, chart.maxLeads)) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col-reverse gap-0.5 group relative" title={`${d.date}: ${d.sold} sold, ${d.leads} leads`}>
                  <div className="bg-[#10a37f] rounded-t" style={{ height: `${soldH}%` }} />
                  <div className="bg-[#006DFF]/30 rounded-t" style={{ height: `${leadH}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] font-bold text-[#6b7c93] mt-1.5">
            <span>{chart.series[0]?.date.slice(5) || ""}</span>
            <span>{chart.series[chart.series.length - 1]?.date.slice(5) || ""}</span>
          </div>
        </div>

        {/* Conversion by source */}
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4" data-testid="metric-by-source">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93]">Conversion by source</div>
              <div className="font-extrabold text-[var(--ssc-navy)]">Where deals come from</div>
            </div>
            <Target className="text-[var(--ssc-blue)]" size={18} />
          </div>
          {data.by_source.length === 0 ? (
            <div className="text-sm text-[#6b7c93]">No leads in this window.</div>
          ) : (
            <ul className="space-y-2.5">
              {data.by_source.slice(0, 8).map((s) => {
                const widthPct = (s.leads / totalSourceLeads) * 100;
                return (
                  <li key={s.source} data-testid={`source-${s.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-extrabold text-[var(--ssc-navy)] truncate">{s.source}</span>
                      <span className="tabular-nums text-[#475569] font-bold">
                        {s.leads} lead{s.leads === 1 ? "" : "s"} · <span style={{ color: s.conversion_pct >= 10 ? "#10a37f" : "var(--ssc-ink-2)" }}>{s.conversion_pct}%</span>
                      </span>
                    </div>
                    <div className="h-2 mt-1 rounded-full bg-[#F4F7FB] overflow-hidden flex">
                      <div className="bg-[#10a37f]" style={{ width: `${(s.sold / s.leads) * widthPct}%` }} title={`${s.sold} sold`} />
                      <div className="bg-[var(--ssc-blue)]/40" style={{ width: `${(s.working / s.leads) * widthPct}%` }} />
                      <div className="bg-[#ef4444]/30" style={{ width: `${(s.lost / s.leads) * widthPct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Bottom row: alerts + push */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4 flex items-start gap-3" data-testid="metric-response-card">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--ssc-blue)] text-white flex items-center justify-center">
            <Clock size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93]">Response coverage</div>
            <div className="text-xl font-black text-[var(--ssc-navy)] mt-0.5">
              {data.response_time.responded_count} / {data.total_leads} responded
            </div>
            <div className="text-xs text-[#475569] mt-0.5">
              Avg {fmtDuration(data.response_time.avg_seconds)} · {data.response_time.unresponded_count} still cold
            </div>
            {data.response_time.unresponded_count > 0 && (
              <div className="text-xs font-bold text-[var(--ssc-red)] mt-1 inline-flex items-center gap-1">
                <AlertTriangle size={12} /> Cold leads need a first touch
              </div>
            )}
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-[#D9E1EA] p-4 flex items-start gap-3" data-testid="metric-push-card">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#10a37f] text-white flex items-center justify-center">
            <Bell size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black uppercase tracking-wider text-[#6b7c93]">Active push subscribers</div>
            <div className="text-xl font-black text-[var(--ssc-navy)] mt-0.5">{data.push_subscribers}</div>
            <div className="text-xs text-[#475569] mt-0.5">Get an instant ping when a matching vehicle hits the lot.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// silence unused-import warnings for the icons we may swap in later
void [CheckCircle2, fmtMoney];
